import type { ReactNode } from 'react';
import './AppHeader.css';

export type AppHeaderTheme = 'navy' | 'green' | 'white';

export interface AppHeaderProps {
  /** Background/text treatment. Matches the three brand header variants. Defaults to "navy". */
  theme?: AppHeaderTheme;
  /** Extra class names merged onto the header element. */
  className?: string;
  /** Slot children — typically `AppHeader.Brand`, `AppHeader.Nav`, `AppHeader.Actions`. */
  children: ReactNode;
}

export interface AppHeaderSlotProps {
  className?: string;
  children: ReactNode;
}

function cx(base: string, extra?: string): string {
  return extra ? `${base} ${extra}` : base;
}

function AppHeaderRoot({ theme = 'navy', className, children }: AppHeaderProps) {
  return (
    <header className={cx(`alc-header alc-header-${theme}`, className)}>
      {children}
    </header>
  );
}

/** Left region — wordmark / logo. Pushes the remaining slots to the right. */
function Brand({ className, children }: AppHeaderSlotProps) {
  return <div className={cx('alc-header-brand', className)}>{children}</div>;
}

/** Navigation region — rendered as a `<nav>` landmark. */
function Nav({ className, children }: AppHeaderSlotProps) {
  return <nav className={cx('alc-header-nav', className)}>{children}</nav>;
}

/** Right-hand region — buttons, user menu, toggles. */
function Actions({ className, children }: AppHeaderSlotProps) {
  return <div className={cx('alc-header-actions', className)}>{children}</div>;
}

/**
 * A themeable app header shell. Compose the regions with the attached slot
 * components:
 *
 * ```tsx
 * <AppHeader theme="navy">
 *   <AppHeader.Brand><img src={wordmark} alt="My App" /></AppHeader.Brand>
 *   <AppHeader.Nav>{navLinks}</AppHeader.Nav>
 *   <AppHeader.Actions>{userMenu}</AppHeader.Actions>
 * </AppHeader>
 * ```
 */
export const AppHeader = Object.assign(AppHeaderRoot, { Brand, Nav, Actions });
